export type PremiumWriteAccess =
  | { allowed: true; reason: "entitled" | "product_trial" | "legacy_unknown" }
  | { allowed: false; reason: "trial_expired" | "not_entitled" };

export function resolvePremiumWriteAccess(input: {
  entitlementStatus?: string | null;
  entitlementPlanId?: string | null;
  entitlementPlanSource?: string | null;
  entitlementPeriodEnd?: string | null;
  accountMode?: string | null;
  profileTrialEndsAt?: string | null;
  requiresExplicitEntitlement?: boolean;
  now?: Date;
}): PremiumWriteAccess {
  const now = input.now ?? new Date();

  const hasIntelligencePlan = input.entitlementPlanId === "intelligence"
    || input.entitlementPlanId === "intelligence_solo"
    || input.entitlementPlanId === "intelligence_plus";

  if (input.entitlementStatus === "allowlisted"
    || (input.entitlementStatus === "active" && hasIntelligencePlan)) {
    return { allowed: true, reason: "entitled" };
  }

  if (hasIntelligencePlan
    && input.entitlementStatus === "trialing"
    && input.entitlementPlanSource === "manual") {
    return isFuture(input.entitlementPeriodEnd, now)
      ? { allowed: true, reason: "product_trial" }
      : { allowed: false, reason: "trial_expired" };
  }

  if (hasIntelligencePlan && input.entitlementStatus === "trialing") {
    return { allowed: true, reason: "entitled" };
  }

  if (input.entitlementStatus === "active"
    && (input.entitlementPlanId === "leadership_record"
      || input.entitlementPlanId === "leadership_record_solo")) {
    return { allowed: false, reason: "not_entitled" };
  }

  if (input.requiresExplicitEntitlement) {
    return { allowed: false, reason: "not_entitled" };
  }

  if (input.accountMode === "solo_trial" && input.profileTrialEndsAt) {
    return isFuture(input.profileTrialEndsAt, now)
      ? { allowed: true, reason: "product_trial" }
      : { allowed: false, reason: "trial_expired" };
  }

  return { allowed: true, reason: "legacy_unknown" };
}

export function hasCurrentSubscriptionPremiumAccess(input: {
  status?: string | null;
  currentPeriodEnd?: string | null;
  paymentFailureGraceEndsAt?: string | null;
  promoCodeUsed?: string | null;
  now?: Date;
}): boolean {
  const now = input.now ?? new Date();
  if (input.promoCodeUsed === "manual_unlimited_access") {
    return input.status === "active" || input.status === "trialing" || input.status === "past_due";
  }
  if (input.status === "active") {
    return !input.currentPeriodEnd || isFuture(input.currentPeriodEnd, now);
  }
  if (input.status === "trialing") {
    return isFuture(input.currentPeriodEnd, now);
  }
  if (input.status === "past_due") {
    return isFuture(input.paymentFailureGraceEndsAt, now);
  }
  return false;
}

function isFuture(value: string | null | undefined, now: Date): boolean {
  if (!value) return false;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) && timestamp > now.getTime();
}
